"use client";

import { c as _c } from "react/compiler-runtime";
import { CarouselTabItem, CarouselTabs } from "@/components/common/carousel-tabs";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { m } from "framer-motion";
import { Building, Home } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { BuyerJourneyPhases } from "./buyer-journey";
import { ProcessFlow } from "./process-flow";
import { SellerJourneyPhases } from "./seller-journey";
import { HowItWorksProps } from "./types";
const tabs: CarouselTabItem[] = [{
  id: "buy",
  label: "Buy Property",
  icon: <Home className="w-5 h-5" />,
  content: <ProcessFlow phases={BuyerJourneyPhases} />
}, {
  id: "sell",
  label: "Sell Property",
  icon: <Building className="w-5 h-5" />,
  content: <ProcessFlow phases={SellerJourneyPhases} />
}];
export const HowItWorks: React.FC<HowItWorksProps> = t0 => {
  const $ = _c(39);
  const {
    defaultTab: t1,
    relationship
  } = t0;
  const defaultTab = t1 === undefined ? "buy" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      threshold: 0.8,
      triggerOnce: true
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    ref: titleRef,
    isInView: titleInView
  } = useScrollAnimation(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      threshold: 0.5,
      triggerOnce: true
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    ref: tabsRef,
    isInView: tabsInView
  } = useScrollAnimation(t3);
  const searchParams = useSearchParams();
  let t4;
  if ($[2] !== defaultTab || $[3] !== searchParams) {
    t4 = searchParams?.get("tab") ?? defaultTab;
    $[2] = defaultTab;
    $[3] = searchParams;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const tab = t4;
  let t5;
  if ($[5] !== relationship || $[6] !== tab) {
    let t6;
    if ($[8] !== relationship) {
      t6 = t7 => {
        const {
          id
        } = t7;
        return !relationship || id === relationship;
      };
      $[8] = relationship;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== tab) {
      t7 = t8 => {
        const {
          id: id_0,
          content,
          ...rest
        } = t8;
        return {
          content: id_0 === tab ? content : null,
          id: id_0,
          ...rest
        };
      };
      $[10] = tab;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t5 = tabs.filter(t6).map(t7);
    $[5] = relationship;
    $[6] = tab;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const tabsListed = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 20
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== titleInView) {
    t7 = titleInView ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    };
    $[13] = titleInView;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      duration: 0.4,
      ease: "easeOut",
      type: "tween"
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = titleInView ? "transform, opacity" : "auto";
  let t10;
  if ($[16] !== t9) {
    t10 = {
      willChange: t9
    };
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7 || $[20] !== titleRef) {
    t11 = <m.h2 ref={titleRef} className="text-3xl md:text-4xl font-bold text-foreground text-center mb-6 md:mb-8" initial={t6} animate={t7} transition={t8} style={t10}>How it works?</m.h2>;
    $[18] = t10;
    $[19] = t7;
    $[20] = titleRef;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: 0,
      y: 15
    };
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== tabsInView) {
    t13 = tabsInView ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 15
    };
    $[23] = tabsInView;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      duration: 0.4,
      ease: "easeOut",
      delay: 0.1,
      type: "tween"
    };
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = tabsInView ? "transform, opacity" : "auto";
  let t16;
  if ($[26] !== t15) {
    t16 = {
      willChange: t15
    };
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== defaultTab || $[29] !== tabsListed) {
    t17 = <CarouselTabs tabs={tabsListed} defaultSelectedKey={defaultTab} variant="pills" size="md" className="w-full" itemClassName="min-h-[0px]" urlQueryParam="tab" usePushState={true} />;
    $[28] = defaultTab;
    $[29] = tabsListed;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t13 || $[32] !== t16 || $[33] !== t17 || $[34] !== tabsRef) {
    t18 = <m.div ref={tabsRef} className="flex justify-center mb-8 md:mb-12" initial={t12} animate={t13} transition={t14} style={t16}>{t17}</m.div>;
    $[31] = t13;
    $[32] = t16;
    $[33] = t17;
    $[34] = tabsRef;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t11 || $[37] !== t18) {
    t19 = <section className="py-12 md:py-16 bg-background max-w-7xl mx-auto"><div className="container px-4 mx-auto max-w-7xl">{t11}{t18}</div></section>;
    $[36] = t11;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  return t19;
};